import * as React from 'react';

import { Clock, FolderTree, Home, Receipt, Wrench, GitCommit } from 'lucide-react';

import { Button } from '@/components/ui/button';

export type MobileDeveloperSectionId = 'overview' | 'invoices' | 'commits' | 'releases' | 'time';

type SectionDef = {
  id: MobileDeveloperSectionId;
  label: string;
  icon: React.ReactNode;
};

const ALL_SECTIONS: SectionDef[] = [
  { id: 'overview', label: 'Overview', icon: <Home className="h-4 w-4" /> },
  { id: 'invoices', label: 'Invoices', icon: <Receipt className="h-4 w-4" /> },
  { id: 'commits', label: 'Commits', icon: <GitCommit className="h-4 w-4" /> },
  { id: 'releases', label: 'Releases', icon: <FolderTree className="h-4 w-4" /> },
  { id: 'time', label: 'Time', icon: <Clock className="h-4 w-4" /> },
];

export function MobileDeveloperToolsBar({
  enabled,
  onOpenTools,
  locked,
}: {
  enabled: boolean;
  onOpenTools: () => void;
  locked: boolean;
}) {
  return (
    <div
      data-testid="mobile-developer-tools-bar"
      className={`mb-4 flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-card/40 px-3 py-2 ${!enabled ? 'hidden' : ''}`}
      style={{ display: enabled ? 'flex' : 'none' }}
    >
      <div className="min-w-0">
        <p className="text-sm font-medium leading-5">Developer</p>
        <p className="text-xs text-muted-foreground truncate">{locked ? 'Locked' : 'Unlocked'}</p>
      </div>
      <Button type="button" variant="outline" size="sm" className="gap-2" onClick={onOpenTools}>
        <Wrench className="h-4 w-4" />
        Tools
      </Button>
    </div>
  );
}

export function MobileDeveloperBottomNav({
  enabled,
  active,
  available,
  onChange,
}: {
  enabled: boolean;
  active: MobileDeveloperSectionId;
  available: MobileDeveloperSectionId[];
  onChange: (id: MobileDeveloperSectionId) => void;
}) {
  const sections = ALL_SECTIONS.filter((section) => available.includes(section.id));

  return (
    <nav
      data-testid="mobile-developer-bottom-nav"
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-border/60 bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/70"
      style={{ display: enabled ? 'block' : 'none' }}
    >
      <div className="mx-auto max-w-screen-md px-2 pb-[var(--safe-area-bottom,0px)]">
        <div className="grid" style={{ gridTemplateColumns: `repeat(${Math.max(1, sections.length)}, minmax(0, 1fr))` }}>
          {sections.map((section) => {
            const selected = section.id === active;
            return (
              <Button
                key={section.id}
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange(section.id)}
                className={`h-12 flex-col gap-1 rounded-none ${selected ? 'text-foreground' : 'text-muted-foreground'}`}
                aria-current={selected ? 'page' : undefined}
              >
                <div className="flex items-center justify-center">{section.icon}</div>
                <span className="text-[10px] leading-3 tracking-wide">{section.label}</span>
                <span className={selected ? 'h-[2px] w-6 rounded-full bg-foreground/70' : 'h-[2px] w-6 rounded-full bg-transparent'} />
              </Button>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
